/**
 * Стена Рублокса — своя лента, отдельная от школьной.
 *
 * Живёт в СВОЁМ бэкенде (`api.rublox.pro`), а не в микросервисах школы:
 * содержимое у нас своё, и завязывать его на `social` школы значило бы
 * смешать две разные ленты.
 *
 * Модерация обязательна: запись публикуется только после проверки —
 * правило проекта, самоодобрения нет. Автор видит свои записи в любом
 * статусе, остальные — только одобренные.
 */

import { getToken, refreshOnce, getRefreshToken, ApiError } from "./client";

/**
 * ⚠️ Адрес АБСОЛЮТНЫЙ и это не случайность: путь `/api/v2/...` на
 * rublox.pro уходит в SPA-fallback и отдаёт HTML вместо JSON. Тот же
 * механизм ломал загрузку аватаров («Unexpected token '<'»).
 */
export const WALL_BASE = "https://api.rublox.pro/api/v2/rublox";

async function call(path, options = {}) {
  const {
    method = "GET",
    body,
    auth = true,
    signal,
    _retried = false,
  } = options;

  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = token;
  }

  let res;
  try {
    res = await fetch(WALL_BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    if (e.name === "AbortError") throw e;
    throw new ApiError("Не удалось связаться с сервером. Проверьте интернет.", 0);
  }

  // 401 → обновляем токен и повторяем ровно один раз.
  if (res.status === 401 && auth && !_retried && getRefreshToken()) {
    const ok = await refreshOnce();
    if (ok) return call(path, { ...options, _retried: true });
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }

  if (!res.ok) {
    const msg = (data && (data.detail || data.message)) || messageFor(res.status);
    throw new ApiError(typeof msg === "string" ? msg : "Ошибка запроса", res.status);
  }
  return data;
}

function messageFor(status) {
  switch (status) {
    case 401: return "Сессия истекла. Войди заново.";
    case 403: return "Публикация записей сейчас недоступна.";
    case 404: return "Запись не найдена.";
    case 429: return "Слишком часто. Подожди немного.";
    case 503: return "Сервис временно недоступен. Попробуй ещё раз.";
    default:  return "Не удалось выполнить действие.";
  }
}

/* ── лента ────────────────────────────────────────────────────────── */

/**
 * Лента стены.
 * @param {object} opts
 *   cursor — курсор следующей страницы (keyset-пагинация, не offset);
 *   mine   — свои записи в любом статусе, включая «на модерации».
 * @returns {Promise<{posts: Array, next_cursor: string|null}>}
 */
export async function getWall({ cursor, limit = 20, mine = false, signal } = {}) {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (limit) params.set("limit", String(limit));
  if (mine) params.set("mine", "true");
  const qs = params.toString();
  return call(`/wall${qs ? "?" + qs : ""}`, { signal });
}

/**
 * Опубликовать запись. Уходит на модерацию — в ленте появится после
 * проверки, поэтому интерфейс должен об этом честно предупреждать.
 */
export async function createPost(text) {
  return call("/wall", { method: "POST", body: { text } });
}

/**
 * Засчитать просмотр. Считается один раз на пользователя — повторные
 * вызовы безопасны, счётчик не накрутится.
 */
export async function registerView(postId) {
  return call(`/wall/${postId}/view`, { method: "POST" });
}

/* ── модерация (только админ/модератор) ───────────────────────────── */

export async function moderatePost(postId, approve, reason) {
  return call(`/moderation/wall/${postId}`, {
    method: "POST",
    body: { approve, reason: reason || null },
  });
}

/* ── вспомогательное ──────────────────────────────────────────────── */

/** «5 минут назад» — короткая форма времени для ленты. */
export function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));

  if (sec < 60) return "только что";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} ${plural(min, "минуту", "минуты", "минут")} назад`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} ${plural(hours, "час", "часа", "часов")} назад`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ${plural(days, "день", "дня", "дней")} назад`;

  return new Date(iso).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });
}

/** Русское склонение числительных: 1 минуту, 2 минуты, 5 минут. */
function plural(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}
